import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, computed, DestroyRef, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiIconComponent } from '@siemens/element-ng/icon';
import {
  SiSelectComponent,
  SiSelectLazyOptionsDirective,
  SiSelectSingleValueDirective
} from '@siemens/element-ng/select';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { SiTooltipModule } from '@siemens/element-ng/tooltip';

import { EventService } from '../../../../../shared/services/event.service';
import { ScheduleService } from '../../../schedule/schedule.service';
import { Station, Waypoint } from './map-planner.model';
import { StationSelectSource } from './station-select-source';

/** A station handed to the map, in the order it should be routed through. */
export interface PlannedRoutePoint {
  lat: number;
  lng: number;
  name: string;
}

@Component({
  selector: 'app-map-planner',
  imports: [
    DragDropModule,
    FormsModule,
    TranslateModule,
    SiIconComponent,
    SiSelectComponent,
    SiSelectSingleValueDirective,
    SiSelectLazyOptionsDirective,
    SiTooltipModule
  ],
  standalone: true,
  templateUrl: './map-planner.component.html',
  styleUrl: './map-planner.component.scss'
})
export class MapPlannerComponent {
  readonly routeGenerated = output<PlannedRoutePoint[]>();
  readonly routeCleared = output<void>();

  private readonly toastService = inject(SiToastNotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly scheduleService = inject(ScheduleService);
  private readonly eventService = inject(EventService);

  private readonly sharedStationCache = new Map<string, Station>();

  readonly startStationSource = new StationSelectSource(
    this.scheduleService,
    this.sharedStationCache,
    () => this.endStationId()
  );
  readonly endStationSource = new StationSelectSource(
    this.scheduleService,
    this.sharedStationCache,
    () => this.startStationId()
  );
  newWaypointSource: StationSelectSource | null = null;

  readonly startStationId = signal<string | undefined>(undefined);
  readonly endStationId = signal<string | undefined>(undefined);
  readonly startStation = signal<Station | null>(null);
  readonly endStation = signal<Station | null>(null);
  readonly waypoints = signal<Waypoint[]>([]);
  readonly isAddingWaypoint = signal(false);
  readonly newWaypointStationId = signal<string | undefined>(undefined);

  readonly canGenerateRoute = computed(() => {
    return this.startStation() !== null && this.endStation() !== null;
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.cleanupSources());
    this.eventService.loadTourMapRouteEvent$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ start, end }) => {
        this.startStationSource.addStationToCache(start);
        this.endStationSource.addStationToCache(end);
        this.startStationId.set(start.id.toString());
        this.endStationId.set(end.id.toString());
        this.startStation.set(start);
        this.endStation.set(end);
        this.findRoute();
      });
  }

  private cleanupSources(): void {
    this.startStationSource.cleanup();
    this.endStationSource.cleanup();
    this.waypoints().forEach(wp => wp.source?.cleanup());
    this.newWaypointSource?.cleanup();
    this.sharedStationCache.clear();
  }

  private validateAndSelectStation(
    stationId: string | undefined,
    source: StationSelectSource,
    checkDuplicate: boolean = true
  ): Station | null {
    if (!stationId) {
      return null;
    }

    if (checkDuplicate && this.isStationAlreadyUsed(stationId)) {
      this.showErrorToast('MAP_PLANNER.ERROR.STATION_ALREADY_USED');
      return null;
    }

    const station = source.getStationById(stationId);
    if (!station) {
      this.showErrorToast('MAP_PLANNER.ERROR.STATION_NOT_FOUND');
      return null;
    }

    if (!this.isValidCoordinates(station)) {
      this.showErrorToast('MAP_PLANNER.ERROR.INVALID_COORDINATES');
      return null;
    }

    return station;
  }

  onStartStationChange(stationId: string | undefined): void {
    if (!stationId) {
      this.startStationId.set(undefined);
      this.startStation.set(null);
      return;
    }

    const station = this.validateAndSelectStation(stationId, this.startStationSource);
    if (station) {
      this.startStationId.set(stationId);
      this.startStation.set(station);
    }
  }

  onEndStationChange(stationId: string | undefined): void {
    if (!stationId) {
      this.endStationId.set(undefined);
      this.endStation.set(null);
      return;
    }

    const station = this.validateAndSelectStation(stationId, this.endStationSource);
    if (station) {
      this.endStationId.set(stationId);
      this.endStation.set(station);
    }
  }

  showAddWaypoint(): void {
    this.isAddingWaypoint.set(true);
    this.newWaypointStationId.set(undefined);
    this.newWaypointSource?.cleanup();
    this.newWaypointSource = new StationSelectSource(this.scheduleService, this.sharedStationCache);
  }

  cancelAddWaypoint(): void {
    this.isAddingWaypoint.set(false);
    this.newWaypointStationId.set(undefined);
  }

  onWaypointStationChange(stationId: string | undefined): void {
    if (!stationId) return;
    this.newWaypointStationId.set(stationId);
    this.confirmAddWaypoint();
  }

  confirmAddWaypoint(): void {
    const stationId = this.newWaypointStationId();
    if (!stationId || !this.newWaypointSource) return;

    const station = this.validateAndSelectStation(stationId, this.newWaypointSource);
    if (!station) return;

    const newWaypoint: Waypoint = {
      id: this.generateWaypointId(),
      station,
      source: this.newWaypointSource
    };

    this.waypoints.update(wps => [...wps, newWaypoint]);
    this.newWaypointSource = new StationSelectSource(this.scheduleService, this.sharedStationCache);
    this.isAddingWaypoint.set(false);
    this.newWaypointStationId.set(undefined);
  }

  removeWaypoint(waypointId: string): void {
    const waypoint = this.waypoints().find(wp => wp.id === waypointId);
    if (!waypoint) return;

    waypoint.source?.cleanup();
    this.waypoints.update(wps => wps.filter(wp => wp.id !== waypointId));
  }

  onWaypointDrop(event: CdkDragDrop<Waypoint[]>): void {
    const waypointsArray = [...this.waypoints()];
    moveItemInArray(waypointsArray, event.previousIndex, event.currentIndex);
    this.waypoints.set(waypointsArray);
  }

  clearRoute(): void {
    this.startStation.set(null);
    this.startStationId.set(undefined);
    this.endStation.set(null);
    this.endStationId.set(undefined);
    this.waypoints().forEach(wp => wp.source?.cleanup());
    this.waypoints.set([]);
    this.isAddingWaypoint.set(false);
    this.newWaypointStationId.set(undefined);
    this.startStationSource.clearCache();
    this.endStationSource.clearCache();
    this.routeCleared.emit();
  }

  findRoute(): void {
    if (!this.canGenerateRoute()) {
      this.showErrorToast('MAP_PLANNER.ERROR.SELECT_STATIONS');
      return;
    }

    this.routeGenerated.emit(this.buildRoutePoints());
  }

  private buildRoutePoints(): PlannedRoutePoint[] {
    const points: PlannedRoutePoint[] = [];

    const start = this.startStation();
    if (start) {
      points.push({ lat: start.lat, lng: start.lon, name: start.stationName });
    }

    this.waypoints().forEach(wp => {
      points.push({ lat: wp.station.lat, lng: wp.station.lon, name: wp.station.stationName });
    });

    const end = this.endStation();
    if (end) {
      points.push({ lat: end.lat, lng: end.lon, name: end.stationName });
    }

    if (points.length < 2) {
      throw new Error('Route must have at least start and end stations');
    }

    return points;
  }

  private isValidCoordinates(station: Station | null): boolean {
    if (!station) return false;
    const { lat, lon } = station;
    if (typeof lat !== 'number' || typeof lon !== 'number') return false;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false;
    return true;
  }

  private isStationAlreadyUsed(stationId: string): boolean {
    if (this.startStationId() === stationId) return true;
    if (this.endStationId() === stationId) return true;
    return this.waypoints().some(wp => wp.station.id.toString() === stationId);
  }

  private generateWaypointId(): string {
    return `wp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private showErrorToast(messageKey: string): void {
    this.toastService.showToastNotification({
      state: 'danger',
      title: this.translateService.instant('MAP_PLANNER.ERROR.TITLE'),
      message: this.translateService.instant(messageKey)
    });
  }
}
